import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from 'src/app/service/events/dashboard.service';
import { BaseForm } from 'src/app/view/base-form';
import { TrazabilidadApiService } from 'src/app/service/api/trazabilidad-api.service';
import { TrazabilidadFormDefinition } from './form-control/form-definition';
import { TrazabilidadModel } from 'src/app/models/stock/trazabilidad.model';
import { FincasApiService } from 'src/app/service/api/fincas-api.service';
import { VentasApiService } from 'src/app/service/api/ventas-api.service';
import { FormCommonApiService } from 'src/app/service/api/formcommon-api.service';
import {
    ReactiveFormChangedField,
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';

@Component({
    selector: 'app-trazabilidad-form',
    standalone: false,
    templateUrl: './trazabilidad-form.component.html',
    styleUrls: ['./trazabilidad-form.component.scss']
})
export class TrazabilidadFormComponent extends BaseForm<TrazabilidadModel> implements OnInit {

    public form!: TrazabilidadFormDefinition;

    public readonly reactiveFormLoadingLabel = 'Preparando formulario de trazabilidad...';

    public isReactiveFormReady = false;

    public reactiveFormLoadingError = '';

    public initialPreviousData: TrazabilidadModel | null = null;

    public reactiveFormRecordId = '';

    constructor(
        public trazabilidadApi: TrazabilidadApiService,
        public fincasApi: FincasApiService,
        public ventasApi: VentasApiService,
        public formCommonApi: FormCommonApiService,
        public override route: ActivatedRoute,
        public override router: Router,
        public dashboardEvents: DashboardService,
    ) {
        super(
            route,
            router,
            dashboardEvents,
            trazabilidadApi.trazabilidad.PUT,
            trazabilidadApi.trazabilidad.POST,
            'stock-trazabilidad',
            'Editar Trazabilidad',
            'Crear Trazabilidad',
            'Duplicar Trazabilidad'
        );
    }

    public ngOnInit(): void {
        void this.initializeTrazabilidadForm();
    }

    private async initializeTrazabilidadForm(): Promise<void> {
        try {
            this.isReactiveFormReady = false;
            this.reactiveFormLoadingError = '';
            this.resetExternalDropdowns();
            this.softInit(this.getType());
            const routeModel = this.toTrazabilidadModel(this.getModel() as unknown as Record<string, unknown>);
            this.reactiveFormRecordId = this.resolveReactiveFormRecordId(routeModel);
            const initialModel = this.initializeHostModel(routeModel);

            this.initialPreviousData = this.getInitialPreviousData(initialModel);
            this.form = new TrazabilidadFormDefinition(
                this.getType(),
                initialModel as unknown as Record<string, string>,
                null,
                {
                    proveedores: this.ventasApi.compradoresGET,
                    fincas: this.fincasApi.fincasGET,
                    sectores: this.fincasApi.sectoresGET,
                    parcelas: this.fincasApi.parcelasGET,
                    cultivos: this.formCommonApi.cultivosGET,
                    variedades: this.formCommonApi.variedadesGET,
                }
            );
            this.form.model = this.toTrazabilidadFormValue({
                ...this.form.model,
                ...initialModel
            });
            await Promise.resolve();
            this.markReactiveFormReady();
        } catch (error: unknown) {
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            this.finalizeExternalDropdowns();
            console.error('No se ha podido preparar el formulario de trazabilidad.', error);
        }
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<TrazabilidadModel>): void {
        const stableValue = this.captureStableValueChange(event.value as TrazabilidadModel);

        this.form.model = this.toTrazabilidadFormValue(stableValue);
        this.form.tag = event.changedField;
        this.syncModelFromForm();

        if (event.origin !== 'user') {
            return;
        }

        this.handleFormValueChange(event.value, event.changedField);
    }

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<TrazabilidadModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged' && event.type !== 'dropdownRefresh') {
            return;
        }

        const stableValue = this.captureStableValueChange(event.value as TrazabilidadModel);

        this.form.model = this.toTrazabilidadFormValue(stableValue);
        this.form.tag = event.changedField;
        this.syncModelFromForm();
    }

    private handleFormValueChange(
        formModel: ReactiveFormValue<TrazabilidadModel>,
        lastChangedFieldName: ReactiveFormChangedField<TrazabilidadModel>,
    ): void {
        this.form.model = this.toTrazabilidadFormValue(formModel);
        this.form.tag = lastChangedFieldName;
        this.syncModelFromForm();
    }

    private markReactiveFormReady(): void {
        this.isReactiveFormReady = true;
        this.finalizeExternalDropdowns();
        this.refreshExternalDropdownMappings();
    }

    private syncModelFromForm(): void {
        const normalizedModel = this.toTrazabilidadModel(this.form.model as Record<string, unknown>);

        this.form.model = this.toTrazabilidadFormValue(normalizedModel);
        this.updateHostModel(normalizedModel);
    }

    private refreshExternalDropdownMappings(): void {
        this.formBuilderRef?.requestExternalDropdownMappingsRefresh();
    }

    private getInitialPreviousData(routeModel: TrazabilidadModel): TrazabilidadModel | null {
        return Object.keys(routeModel as Record<string, unknown>).length === 0
            ? null
            : this.toTrazabilidadModel(routeModel as unknown as Record<string, unknown>);
    }

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return `No se ha podido cargar el formulario de trazabilidad: ${error.message}`;
        }

        return 'No se ha podido cargar el formulario de trazabilidad. Revisa la conexión y vuelve a intentarlo.';
    }

    private toTrazabilidadFormValue(
        value: Record<string, unknown> | ReactiveFormValue<TrazabilidadModel> | TrazabilidadModel,
    ): ReactiveFormValue<TrazabilidadModel> {
        const normalizedValue = {
            ...(value as Record<string, unknown>),
            maceta_reciclada: this.toBinarySwitchValue((value as Record<string, unknown>)['maceta_reciclada']),
        };

        return normalizedValue as ReactiveFormValue<TrazabilidadModel>;
    }

    private toTrazabilidadModel(value: Record<string, unknown> | ReactiveFormValue<TrazabilidadModel> | TrazabilidadModel): TrazabilidadModel {
        return { ...(value as Record<string, unknown>) } as unknown as TrazabilidadModel;
    }

    private toBinarySwitchValue(value: unknown): 0 | 1 {
        if (value === true || value === 1) {
            return 1;
        }

        if (typeof value === 'string') {
            const normalizedValue = value.trim().toLowerCase();

            return normalizedValue === '1' || normalizedValue === 'true' || normalizedValue === 'sí' || normalizedValue === 'si' ? 1 : 0;
        }

        return 0;
    }
}
